import { memo, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { useFullscreen } from "@/lib/hooks/useFullscreen";
import { fmtInt } from "@/lib/format";

import { Icon } from "@/components/icons";
/** THE table (U2) — every data table renders through this, never a raw
 *  `<table className="tbl">`. Carries the measured-incident fixes so no future
 *  table can regress them:
 *
 * - `table-layout: fixed` + explicit `<colgroup>` widths: row mounts are
 *    row-local layout work. Auto layout (`max-content`) re-measured the ENTIRE
 *    table whenever content mounted — measured at 3–5 s of main-thread stall
 *    for a 150×40 window replace on the live tail.
 * - Sticky header row (column names never scroll away) and optional sticky
 *    first column, with the corner cell pinned on both axes.
 * - Memoized rows: with identity-stable `cols` (consumer's job — useMemo or
 *    module-const) and per-row object identity preserved across updates
 *    (React Query structural sharing / append-only buffers), only changed
 *    rows re-render and only new rows mount.
 * - Full-screen mode (⛶ / Esc) via useFullscreen.
 * - Cells ellipsize; pass `title` for the full value on hover.
 * - Optional `pageSize` pagination: CELLS ARE THE MOUNT BUDGET (law 7) —
 *    a 5000-row window × 45 columns is ~225k cells, a measured multi-second
 *    freeze. Any consumer whose row count is unbounded (History windows,
 *    search results) MUST page; the pager mounts one page, never the world.
 */
export type DataCol<T> = {
  key: string;
  label: ReactNode;
  /** px — fixed-layout column width (default 110) */
  width?: number;
  /** HOW HARD THIS COLUMN FIGHTS FOR ITS SPACE. 1 = never drop (the thing the
   *  row IS: an id, a name), 2 = drop only when genuinely tight, 3 = first to
   *  go. Default 2.
   *
   *  Dattatray: *"lot of the tables have a horizontal scrollbar because we can
   *  not fit information directly on the screen."* Widening the page only
   *  postpones that -- twelve columns at 150 px is 1800 px at ANY viewport, so
   *  the table scrolled BY CONSTRUCTION (`tableLayout: fixed` with
   *  `minWidth: <sum of widths>`). A horizontal scrollbar hides data behind a
   *  gesture nobody makes; dropping the least important column shows the rest.
   *
   *  Columns are dropped lowest-priority-first, and never below two.
   *
   *  THE POLICY EVERY TABLE IN THIS CONSOLE NOW FOLLOWS, written here so a new
   *  column inherits a reason instead of the silent default. Leaving it unset
   *  is not neutral -- it makes a SHA-256 fight an asset id for the same space,
   *  which is how 134 columns ended up equally important:
   *
   *    1  never drops. The column that answers WHICH ROW IS THIS (serial,
   *       IMEI, asset id, name+version), the column the table exists to triage
   *       by, and the ACTIONS column -- a table whose buttons have vanished is
   *       not a degraded table, it is a broken one.
   *    2  the attributes you filter and scan by (state, company, model, kind).
   *    3  first to go: hashes, byte sizes, creation and enrolment dates,
   *       who-did-it, counts that are one click away, long free text,
   *       decorative cells.
   *
   *  Full-screen is always the way back to a dropped column, and the chip that
   *  says how many are hidden is what makes that discoverable. */
  priority?: 1 | 2 | 3;
  render: (row: T) => ReactNode;
  /** hover tooltip, e.g. the untruncated value */
  title?: (row: T) => string | undefined;
  mono?: boolean;
  /** `"right"` for a column of NUMBERS, so the digits line up on the decimal
   *  and a short value cannot be mistaken for a long one at a glance. Carries
   *  `tabular-nums` with it, which is correct HERE and only here — the skill's
   *  rule is tabular in columns, proportional for standalone figures, because
   *  tabular gives every digit the width of a `0` and makes a big display
   *  number look loose. */
  align?: "left" | "right";
  /** marks the start of a new GROUP of columns — draws a divider on its left
   *  edge. A 45-column telemetry dump is one undifferentiated run of digits
   *  without it, and the eye has nothing to count from. */
  groupStart?: boolean;
};

const W_DEFAULT = 110;
const cellStyle: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const stickyCell: CSSProperties = { ...cellStyle, position: "sticky", left: 0, zIndex: 1, background: "var(--surface)", color: "var(--t-mute)" };

/** Per-column cell style. Built from the col, so it is pure and cheap — the
 *  memoized `Row` calls it per cell and a fresh object here costs nothing
 *  next to the render it sits inside. */
function cellFor<T>(c: DataCol<T>, sticky: boolean): CSSProperties {
  const base = sticky ? stickyCell : cellStyle;
  if (!c.align && !c.groupStart) return base;
  return {
    ...base,
    ...(c.align === "right"
      ? { textAlign: "right" as const, fontVariantNumeric: "tabular-nums" }
      : null),
    ...(c.groupStart ? { borderLeft: "1px solid var(--line2)" } : null),
  };
}

function RowInner<T>({ r, cols, stickyFirst, onClick }: {
  r: T; cols: DataCol<T>[]; stickyFirst?: boolean; onClick?: (row: T) => void;
}) {
  return (
    <tr style={onClick ? { cursor: "pointer" } : undefined} onClick={onClick ? () => onClick(r) : undefined}>
      {cols.map((c, i) => (
        <td key={c.key}
          className={c.mono ? "mono" : undefined}
          style={cellFor(c, !!stickyFirst && i === 0)}
          title={c.title?.(r)}>
          {c.render(r)}
        </td>
      ))}
    </tr>
  );
}
// memo() drops the generic — restore it with a cast (standard TS pattern)
const Row = memo(RowInner) as typeof RowInner;

export function DataTable<T>({
  rows, cols: allCols, rowKey, stickyFirst = false, maxHeight = 420,
  fullscreenable = false, onRowClick, empty, toolbar, pageSize, zebra = false,
}: {
  rows: T[];
  /** MUST be identity-stable across renders (module const or useMemo) or row
   *  memoization is defeated. */
  cols: DataCol<T>[];
  /** Stripe alternate rows and let VALUES carry the ink (nulls stay dim).
   *  Opt-in: a wide telemetry dump needs it to be trackable across a
   *  horizontal scroll that puts the time column off screen; a six-column
   *  registry table reads cleaner without. */
  zebra?: boolean;
  rowKey: (row: T) => string | number;
  stickyFirst?: boolean;
  maxHeight?: number;
  fullscreenable?: boolean;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** extra controls rendered left of the full-screen button */
  toolbar?: ReactNode;
  /** mount at most this many rows, with a ‹ › pager — REQUIRED when the row
   *  count is unbounded (cells are the mount budget) */
  pageSize?: number;
}) {
  const { full, setFull, overlayStyle } = useFullscreen();
  // MEASURES ITSELF, NOT THE WINDOW. The rail collapses and expands, so the
  // same table has different widths at one viewport -- a media query cannot
  // see that, and `@container` cannot drop a specific COLUMN.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // IGNORE SMALL CHANGES. Dropping a column changes the table's height,
    // which can add or remove the wrapper's vertical scrollbar, which changes
    // its width by ~15 px, which re-fires this -- a column that sits near the
    // threshold would then flicker on and off, and the browser reports it as
    // "ResizeObserver loop completed with undelivered notifications".
    // A 24 px deadband is wider than any scrollbar and far narrower than a
    // column, so real resizes still register and that feedback path cannot.
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      setAvail((prev) => (Math.abs(w - prev) < 24 ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Drop the least important columns until the rest fit. `avail === 0` means
  // not measured yet (or no ResizeObserver, as in jsdom) and then NOTHING is
  // dropped -- an unmeasured table shows everything and scrolls, which is the
  // old behaviour and the safe default.
  const cols = useMemo(() => {
    const full = allCols.reduce((a, c) => a + (c.width ?? W_DEFAULT), 0);
    if (!avail || full <= avail) return allCols;
    const drop = new Set<number>();
    const order = allCols
      .map((c, i) => ({ i, p: c.priority ?? 2 }))
      .filter((x) => x.p > 1)
      .sort((a, b) => b.p - a.p || b.i - a.i);   // lowest priority, rightmost first
    let w = full;
    for (const { i } of order) {
      if (w <= avail || allCols.length - drop.size <= 2) break;
      w -= allCols[i].width ?? W_DEFAULT;
      drop.add(i);
    }
    return allCols.filter((_, i) => !drop.has(i));
  }, [allCols, avail]);
  const hidden = allCols.length - cols.length;

  const tableWidth = useMemo(() => cols.reduce((a, c) => a + (c.width ?? W_DEFAULT), 0), [cols]);

  // pagination — page is CLAMPED, not reset, when rows shrink (a polling
  // consumer must not yank the user back to page 1 on every refetch)
  const [page, setPage] = useState(0);
  const pages = pageSize ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  const cur = Math.min(page, pages - 1);
  const view = useMemo(
    () => (pageSize ? rows.slice(cur * pageSize, (cur + 1) * pageSize) : rows),
    [rows, pageSize, cur],
  );

  if (rows.length === 0) {
    return empty !== undefined
      ? <>{empty}</>
      : <div className="panel panel-pad cap">No rows.</div>;
  }

  const headCell: CSSProperties = { ...cellStyle, position: "sticky", top: 0, zIndex: 2, background: "var(--well)" };
  // A right-aligned column's HEADER has to move with it. A label sitting left
  // of the digits it names reads as belonging to the column before it, which
  // on a 45-column dump is worse than no alignment at all.
  const headFor = (c: DataCol<T>): CSSProperties => ({
    ...headCell,
    ...(c.align === "right" ? { textAlign: "right" as const } : null),
    ...(c.groupStart ? { borderLeft: "1px solid var(--line2)" } : null),
  });
  const headCorner: CSSProperties = { ...headCell, left: 0, zIndex: 4 };

  return (
    <div style={overlayStyle}>
      {(toolbar || fullscreenable || pages > 1 || hidden > 0) && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 6 }}>
          {/* SAY SO WHEN COLUMNS ARE DROPPED. Hiding a column silently is the
              same sin as the scrollbar this replaces -- data present but
              unreachable and unannounced. Full screen is the way back to it,
              so the note sits beside that button. */}
          {hidden > 0 && (
            <span className="chip mute" title="Widen the panel, collapse the sidebar, or open full screen to see every column">
              {hidden} column{hidden === 1 ? "" : "s"} hidden
            </span>
          )}
          {pages > 1 && pageSize && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--t-mute)" }}>
              <button className="btn ghost" disabled={cur === 0} onClick={() => setPage(cur - 1)} title="Previous page">‹</button>
              <span className="mono">
                {fmtInt(cur * pageSize + 1)}–{fmtInt(Math.min((cur + 1) * pageSize, rows.length))} of {fmtInt(rows.length)}
              </span>
              <button className="btn ghost" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} title="Next page">›</button>
            </span>
          )}
          {toolbar}
          {fullscreenable && (
            <button className="btn ghost" onClick={() => setFull((f) => !f)}
              title={full ? "Exit full screen (Esc)" : "Expand the table to the whole window"}>
              <Icon name={full ? "close" : "fullscreen"} /> {full ? "Exit full screen" : "Full screen"}
            </button>
          )}
        </div>
      )}
      <div className="tblwrap" style={{ maxHeight: full ? "none" : maxHeight, flex: full ? 1 : undefined, minHeight: 0, overflow: "auto" }}>
        {/* width:100% + minWidth:sum - narrow tables STRETCH to fill the panel
            (fixed layout distributes the extra across columns); wide ones keep
            their computed width and scroll horizontally. Layout stays fixed =
            row mounts never trigger a whole-table re-measure. */}
        <table className={`tbl${zebra ? " zebra" : ""}`} style={{ tableLayout: "fixed", width: "100%", minWidth: tableWidth }}>
          <colgroup>
            {cols.map((c) => <col key={c.key} style={{ width: c.width ?? W_DEFAULT }} />)}
          </colgroup>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={c.key} style={stickyFirst && i === 0 ? headCorner : headFor(c)}
                  title={typeof c.label === "string" ? c.key : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r) => (
              <Row key={rowKey(r)} r={r} cols={cols} stickyFirst={stickyFirst} onClick={onRowClick} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
